// ============================================================
// ExpressAsyncErrorHandler.jsx
//
// A branded, responsive course content page for the
// "Async Error Wrappers" lesson.
//
// Author: ProDev Architecture Series
// Features: Higher-order function (HOF) utility, middleware
//           integration, and navigation bridge to Dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import AutoFixHighIcon        from "@mui/icons-material/AutoFixHigh";
import PsychologyIcon        from "@mui/icons-material/Psychology";
import BugReportIcon          from "@mui/icons-material/BugReport";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import CodeIcon              from "@mui/icons-material/Code";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressAsyncErrorHandler() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Efficiency · Module 19"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Async <span>Error Wrappers</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Repeating try/catch blocks in every route is tedious and error-prone. By using a higher-order wrapper function, you can automatically forward asynchronous failures to your global error handler.
                    </Typography>
                </Box>

                {/* 1. THE WRAPPER */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<PsychologyIcon />} label="1. The Utility Function" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        This higher-order function takes your route handler and ensures that any rejected promise is caught and passed to <code style={{color: BRAND.white}}>next()</code>.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "// utils/asyncHandler.js", comment: "" },
                        { cmd: "module.exports = (fn) => (req, res, next) => {", comment: "" },
                        { cmd: "  Promise.resolve(fn(req, res, next)).catch(next);", comment: "Auto-forward errors to next()" },
                        { cmd: "};", comment: "" },
                    ]} />
                </Box>

                {/* 2. IMPLEMENTATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AutoFixHighIcon />} label="2. Using it in Routes" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Wrap your async handlers to keep the code flat and clean. You no longer need local try/catch blocks for standard error forwarding.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const asyncHandler = require('./utils/asyncHandler');", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "app.get('/data', asyncHandler(async (req, res) => {", comment: "Wrapped Handler" },
                        { cmd: "  const results = await fetchDataFromDb();", comment: "If this fails, catch(next) handles it" },
                        { cmd: "  res.json(results);", comment: "" },
                        { cmd: "}));", comment: "" },
                    ]} />
                </Box>

                {/* 3. SYNERGY */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<BugReportIcon />} label="3. Global Error Integration" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        The wrapper pushes the error down the middleware stack until it hits your centralized handler.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "app.use((err, req, res, next) => {", comment: "Centralized Handler" },
                        { cmd: "  res.status(500).json({ error: err.message });", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* KEY BENEFITS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Why Use Wrappers?" />
                    <Grid container spacing={2}>
                        {[
                            { title: "DRY Principles", desc: "Don't Repeat Yourself—remove boilerplate try/catch blocks.", icon: <CheckCircleOutlineIcon /> },
                            { title: "Readability", desc: "Focus on the business logic instead of error-handling plumbing.", icon: <CodeIcon /> },
                            { title: "Safety", desc: "Guarantees that no unhandled promise rejections crash your node process.", icon: <IntegrationInstructionsIcon /> },
                        ].map((item) => (
                            <Grid item xs={12} md={4} key={item.title}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        {React.cloneElement(item.icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                                        <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14 }}>{item.title}</Typography>
                                    </Box>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "999px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> While you can write your own, the library <code style={{ color: BRAND.goldBright }}>express-async-handler</code> is a battle-tested industry standard that does exactly this. However, understanding the HOF (Higher Order Function) pattern behind it is essential for advanced JavaScript development.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
